/**
 * Typography tokens from the Figma "UI Design System" text styles.
 * Font family: ES Rebond Grotesque.
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

export interface TypeStyle {
  fontFamily: string;
  /**
   * String literal to match React Native's TextStyle['fontWeight'].
   * Use string literals ('400' | '500' | '700'), not numeric, so
   * consumers can use the token directly without casting.
   */
  fontWeight: '400' | '500' | '700';
  fontStyle?: 'italic';
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
}

const FONT_FAMILY = 'ES Rebond Grotesque';

/**
 * Display face for large stat figures ("$0", "1:1") only. The app bundles a
 * figures-and-symbols subset and loads it at runtime, so it never embeds in the
 * native binary; any letter falls back to the platform font, which is why
 * this face must never carry prose. Mobile only: the app registers it once at
 * its signed-in root, and web and Atlas never load it, so the variant renders
 * the fallback face there.
 */
export const STAT_FIGURE_FONT_FAMILY = 'Hanken Grotesk';

export const typography = {
  displayBold: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 64,
    lineHeight: 68,
    letterSpacing: 0,
  },
  displayLargeBold: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 96,
    lineHeight: 100,
    letterSpacing: 0,
  },
  displayJumboBold: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 128,
    lineHeight: 128,
    letterSpacing: 0,
  },
  h1Bold: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 32,
    lineHeight: 34,
    letterSpacing: 0,
  },
  h1Bold28: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 28,
    lineHeight: 34,
    letterSpacing: 0,
  },
  /** Italic emphasis run paired with h1Bold28 in the Assessment q2 heading. */
  h1Bold28Italic: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontStyle: 'italic' as const,
    fontSize: 28,
    lineHeight: 34,
    letterSpacing: 0,
  },
  // The h1Bold family at the oversized display size used for a chart's headline
  // figure. The Figma node reports a 34px line box, but that is a fixed-height
  // text frame — Figma does not clip glyphs, RN and CSS do. 52px keeps the
  // family's ~1.08 line-height ratio (h1Bold is 32/34) so 48px glyphs render
  // whole; the cost is ~18px more header height than the design frame.
  h1Bold48: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 48,
    lineHeight: 52,
    letterSpacing: 0,
  },
  h1Regular: {
    fontFamily: FONT_FAMILY,
    fontWeight: '400',
    fontSize: 32,
    lineHeight: 38,
    letterSpacing: 0,
  },
  statFigure: {
    fontFamily: STAT_FIGURE_FONT_FAMILY,
    fontWeight: '700',
    fontSize: 32,
    lineHeight: 36,
    letterSpacing: 0,
  },
  h2Bold: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 20,
    lineHeight: 24,
    letterSpacing: 0,
  },
  b1Bold: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: 0.25,
  },
  b1Regular: {
    fontFamily: FONT_FAMILY,
    fontWeight: '400',
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: 0,
  },
  b1BoldItalic: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontStyle: 'italic' as const,
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: 0.25,
  },
  b2Bold: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: 0,
  },
  b2Regular: {
    fontFamily: FONT_FAMILY,
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: 0,
  },
  /**
   * `b2Regular` at the 24px leading used where 16px body sits on the same line
   * as an 18px `b1Bold` run — the taller leading keeps the two baselines
   * aligned. The suffix is the line height, not the size.
   */
  b2Regular24: {
    fontFamily: FONT_FAMILY,
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 24,
    letterSpacing: 0,
  },
  b3Regular: {
    fontFamily: FONT_FAMILY,
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 22,
    letterSpacing: 0,
  },
  b3Bold: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 22,
    letterSpacing: 0,
  },
  c1Regular: {
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0,
  },
  // The Figma style is "c1-medium"; `c1Regular` above predates it and already
  // carries the Medium weight under the Regular name. This is the correctly
  // named token — prefer it in new code; `c1Regular` stays for existing
  // consumers.
  c1Medium: {
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0,
  },
  /** Bold, tracked uppercase label above a card title; the copy supplies the capitals. */
  eyebrow: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 13,
    lineHeight: 18,
    letterSpacing: 1,
  },
  c2Regular: {
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: 0,
  },
  button1: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: 0.25,
  },
  button2: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: 0,
  },
  button3: {
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 22,
    letterSpacing: 0,
  },
} as const satisfies Record<string, TypeStyle>;

export type Typography = typeof typography;
